import React, { useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Radio, Space } from "antd";
import { PrinterOutlined } from "@ant-design/icons";

export interface SchoolReportResponse {
  reportType: string;
  reportCode: string;
  reportTitle: string;
  reportSubtitle: string;
  periodLabel: string;
  schoolId: number;
  schoolName: string;
  schoolAddress: string;
  schoolTaxCode: string;
  month: number;
  year: number;
  schoolYear: string;
  daysInMonth: number;
  viewMode: string;
  summary: Record<string, any>;
  rows: any[];
  secondaryRows?: any[];
}

interface ReportPrintPreviewModalProps {
  open: boolean;
  onCancel: () => void;
  reportData: SchoolReportResponse | null;
}

const fmtNum = (val: any): string => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("vi-VN");
};

const PRINT_IFRAME_ID = "vschool-report-print-iframe";

const ReportPrintPreviewModal: React.FC<ReportPrintPreviewModalProps> = ({
  open,
  onCancel,
  reportData,
}) => {
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("landscape");
  const [preparerName, setPreparerName] = useState<string>("");
  const [chiefAccountantName, setChiefAccountantName] = useState<string>("");
  const [principalName, setPrincipalName] = useState<string>("");
  const printContentRef = useRef<HTMLDivElement>(null);

  const renderedTable = useMemo(() => {
    if (!open || !reportData) return null;
    const rows = reportData.rows || [];
    const sum = reportData.summary || {};
    const type = reportData.reportType;

    switch (type) {
      case "CLASS_FINANCIAL_SUMMARY": {
        const isDetail = reportData.viewMode === "DETAIL";
        const classGroupCount = rows.filter(
          (r: any) => r.rowType === "CLASS_GROUP"
        ).length;
        if (isDetail) {
          return (
            <table className="acc-print-table">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Mã HS</th>
                  <th>Họ và tên học sinh</th>
                  <th>Lớp</th>
                  <th>Khối thu</th>
                  <th>Thừa đầu kỳ (+)</th>
                  <th>Nợ đầu kỳ (-)</th>
                  <th>Phát sinh tháng</th>
                  <th>Thực phải thu</th>
                  <th>Đã thu</th>
                  <th>Còn phải thu</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any, idx: number) => {
                  const isClassGroup = r.rowType === "CLASS_GROUP";
                  return (
                    <tr
                      key={idx}
                      className={isClassGroup ? "total-row" : undefined}
                    >
                      <td className="c">{isClassGroup ? "" : r.stt}</td>
                      <td className="c">{r.studentCode}</td>
                      <td className="l">{r.studentName}</td>
                      <td className="c">{r.className}</td>
                      <td className="l">{r.revenueBlockName}</td>
                      <td className="r">{fmtNum(r.excessCarryAmount)}</td>
                      <td className="r">{fmtNum(r.debtCarryAmount)}</td>
                      <td className="r">{fmtNum(r.monthAmount)}</td>
                      <td className="r">{fmtNum(r.mustCollectAmount)}</td>
                      <td className="r">{fmtNum(r.paidAmount)}</td>
                      <td className="r">{fmtNum(r.remainingAmount)}</td>
                      <td className="c">{r.paymentStatusLabel}</td>
                    </tr>
                  );
                })}
                {classGroupCount > 1 && (
                  <tr className="total-row">
                    <td colSpan={5} className="c">TỔNG CỘNG</td>
                    <td className="r">{fmtNum(sum.excessCarryAmount)}</td>
                    <td className="r">{fmtNum(sum.debtCarryAmount)}</td>
                    <td className="r">{fmtNum(sum.monthAmount)}</td>
                    <td className="r">{fmtNum(sum.mustCollectAmount)}</td>
                    <td className="r">{fmtNum(sum.paidAmount)}</td>
                    <td className="r">{fmtNum(sum.remainingAmount)}</td>
                    <td className="c"></td>
                  </tr>
                )}
              </tbody>
            </table>
          );
        }
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Khối</th>
                <th>Lớp học</th>
                <th>Sĩ số</th>
                <th>HS có ĐK</th>
                <th>Thừa đầu kỳ (+)</th>
                <th>Nợ đầu kỳ (-)</th>
                <th>Phát sinh tháng</th>
                <th>Thực phải thu</th>
                <th>Đã thu</th>
                <th>Còn phải thu</th>
                <th>HS Nợ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.classLevel}</td>
                  <td className="l">{r.className}</td>
                  <td className="r">{fmtNum(r.totalStudents)}</td>
                  <td className="r">{fmtNum(r.registeredStudents)}</td>
                  <td className="r">{fmtNum(r.excessCarryAmount)}</td>
                  <td className="r">{fmtNum(r.debtCarryAmount)}</td>
                  <td className="r">{fmtNum(r.monthAmount)}</td>
                  <td className="r">{fmtNum(r.mustCollectAmount)}</td>
                  <td className="r">{fmtNum(r.paidAmount)}</td>
                  <td className="r">{fmtNum(r.remainingAmount)}</td>
                  <td className="r">{fmtNum(r.debtStudentsCount)}</td>
                </tr>
              ))}
              {rows.length > 1 && (
                <tr className="total-row">
                  <td colSpan={3} className="c">TỔNG CỘNG</td>
                  <td className="r">{fmtNum(sum.totalStudents)}</td>
                  <td className="r">{fmtNum(sum.registeredStudents)}</td>
                  <td className="r">{fmtNum(sum.excessCarryAmount)}</td>
                  <td className="r">{fmtNum(sum.debtCarryAmount)}</td>
                  <td className="r">{fmtNum(sum.monthAmount)}</td>
                  <td className="r">{fmtNum(sum.mustCollectAmount)}</td>
                  <td className="r">{fmtNum(sum.paidAmount)}</td>
                  <td className="r">{fmtNum(sum.remainingAmount)}</td>
                  <td className="r">{fmtNum(sum.debtStudentsCount)}</td>
                </tr>
              )}
            </tbody>
          </table>
        );
      }

      case "INCOME_SOURCE_SUMMARY":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Mã nguồn thu</th>
                <th>Nguồn thu</th>
                <th>Phương thức</th>
                <th>HĐĐT</th>
                <th>Số HS ĐK</th>
                <th>Tổng SL/Suất</th>
                <th>Đơn giá</th>
                <th>Thừa đầu kỳ (+)</th>
                <th>Nợ đầu kỳ (-)</th>
                <th>Phát sinh tháng</th>
                <th>Tổng phải thu</th>
                <th>Đã thu</th>
                <th>Còn nợ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.incomeCode}</td>
                  <td className="l">{r.incomeName}</td>
                  <td className="c">{r.collectByLabel}</td>
                  <td className="c">
                    {Boolean(r.isEinvoice ?? r.einvoice)
                      ? "Có xuất HĐ"
                      : "Không HĐ"}
                  </td>
                  <td className="r">{fmtNum(r.registeredStudents)}</td>
                  <td className="r">{fmtNum(r.expectedUnits)}</td>
                  <td className="r">{fmtNum(r.unitPrice)}</td>
                  <td className="r">{fmtNum(r.excessCarryAmount)}</td>
                  <td className="r">{fmtNum(r.debtCarryAmount)}</td>
                  <td className="r">{fmtNum(r.monthAmount)}</td>
                  <td className="r">{fmtNum(r.mustCollectAmount)}</td>
                  <td className="r">{fmtNum(r.paidAmount)}</td>
                  <td className="r">{fmtNum(r.remainingAmount)}</td>
                </tr>
              ))}
              {rows.length > 1 && (
                <tr className="total-row">
                  <td colSpan={5} className="c">TỔNG CỘNG THEO NGUỒN THU</td>
                  <td className="c">-</td>
                  <td className="r">{fmtNum(sum.expectedUnits)}</td>
                  <td className="c">-</td>
                  <td className="r">{fmtNum(sum.excessCarryAmount)}</td>
                  <td className="r">{fmtNum(sum.debtCarryAmount)}</td>
                  <td className="r">{fmtNum(sum.monthAmount)}</td>
                  <td className="r">{fmtNum(sum.mustCollectAmount)}</td>
                  <td className="r">{fmtNum(sum.paidAmount)}</td>
                  <td className="r">{fmtNum(sum.remainingAmount)}</td>
                </tr>
              )}
            </tbody>
          </table>
        );

      case "STUDENT_DEBT_DETAIL":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Mã HS</th>
                <th>Họ và tên học sinh</th>
                <th>Lớp</th>
                <th>SĐT Phụ huynh</th>
                <th>Nợ kỳ trước</th>
                <th>Phát sinh trong kỳ</th>
                <th>Tổng phải thu</th>
                <th>Đã nộp</th>
                <th>Còn nợ phải thu</th>
                <th>Chi tiết các khoản nợ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.studentCode}</td>
                  <td className="l">{r.studentName}</td>
                  <td className="c">{r.className}</td>
                  <td className="c">{r.parentPhone}</td>
                  <td className="r">{fmtNum(r.debtFromPrevMonth)}</td>
                  <td className="r">{fmtNum(r.monthAmount)}</td>
                  <td className="r">{fmtNum(r.mustCollectAmount)}</td>
                  <td className="r">{fmtNum(r.paidAmount)}</td>
                  <td className="r">{fmtNum(r.remainingDebtAmount)}</td>
                  <td className="l">{r.unpaidItemsSummary}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={5} className="c">
                  TỔNG CỘNG CÔNG NỢ HỌC SINH ({fmtNum(sum.totalDebtStudents)} HS)
                </td>
                <td className="r">{fmtNum(sum.debtFromPrevMonth)}</td>
                <td className="r">{fmtNum(sum.monthAmount)}</td>
                <td className="r">{fmtNum(sum.mustCollectAmount)}</td>
                <td className="r">{fmtNum(sum.paidAmount)}</td>
                <td className="r">{fmtNum(sum.remainingDebtAmount)}</td>
                <td className="c"></td>
              </tr>
            </tbody>
          </table>
        );

      case "CASHBOOK_TRANSACTIONS":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Ngày giờ thu</th>
                <th>Mã biên lai</th>
                <th>Mã GD</th>
                <th>Mã HS</th>
                <th>Họ tên học sinh</th>
                <th>Lớp</th>
                <th>Nguồn thu</th>
                <th>Kênh thanh toán</th>
                <th>Số tiền thực thu</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.paymentDateStr}</td>
                  <td className="c">{r.billId}</td>
                  <td className="c">{r.transactionCode}</td>
                  <td className="c">{r.studentCode}</td>
                  <td className="l">{r.studentName}</td>
                  <td className="c">{r.className}</td>
                  <td className="l">{r.incomeItemsSummary}</td>
                  <td className="c">{r.paymentChannelLabel}</td>
                  <td className="r">{fmtNum(r.amount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={9} className="c">
                  TỔNG CỘNG SỔ QUỸ THỰC THU ({fmtNum(sum.totalTransactions)} giao dịch)
                </td>
                <td className="r">{fmtNum(sum.totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        );

      case "ATTENDANCE_REPORT": {
        const daysCount = reportData.daysInMonth || 31;
        const daysArr = Array.from({ length: daysCount }, (_, i) => i + 1);
        const isDetail = reportData.viewMode === "DETAIL";
        const classGroupRows = rows.filter(
          (r: any) => r.rowType === "CLASS_GROUP"
        );
        const classGroupCount = classGroupRows.length;
        const showTotalRow = isDetail
          ? classGroupCount > 1
          : rows.length > 1;
        const dayTotals: Record<number, number> = {};
        if (showTotalRow) {
          const sourceRows =
            isDetail && classGroupCount > 0
              ? classGroupRows
              : rows.filter((r: any) => r.rowType !== "CLASS_GROUP");
          for (const d of daysArr) {
            dayTotals[d] = 0;
          }
          for (const item of sourceRows) {
            const dv = item.dayValues;
            if (!dv) continue;
            for (const d of daysArr) {
              dayTotals[d] += Number(dv[d] ?? dv[String(d)] ?? 0);
            }
          }
        }
        return (
          <table className="acc-print-table" style={{ fontSize: "8pt" }}>
            <thead>
              <tr>
                <th>STT</th>
                {isDetail ? (
                  <>
                    <th>Mã HS</th>
                    <th>Họ tên học sinh</th>
                    <th>Lớp</th>
                  </>
                ) : (
                  <>
                    <th>Khối</th>
                    <th>Lớp</th>
                    <th>Sĩ số</th>
                  </>
                )}
                {daysArr.map((d) => (
                  <th key={d} style={{ padding: "2px" }}>
                    {String(d).padStart(2, "0")}
                  </th>
                ))}
                <th>Có mặt</th>
                <th>Vắng</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => {
                const isClassGroup = r.rowType === "CLASS_GROUP";
                return (
                  <tr
                    key={idx}
                    className={isClassGroup ? "total-row" : undefined}
                  >
                    <td className="c">{isClassGroup ? "" : r.stt}</td>
                    {isDetail ? (
                      <>
                        <td className="c">{r.studentCode}</td>
                        <td className="l">{r.studentName}</td>
                        <td className="c">{r.className}</td>
                      </>
                    ) : (
                      <>
                        <td className="c">{r.classLevel}</td>
                        <td className="l">{r.className}</td>
                        <td className="r">{r.studentCount}</td>
                      </>
                    )}
                    {daysArr.map((d) => (
                      <td key={d} className="c" style={{ padding: "2px" }}>
                        {r.dayValues?.[d] ?? r.dayValues?.[String(d)] ?? 0}
                      </td>
                    ))}
                    <td className="r">{fmtNum(r.totalPresentUnits)}</td>
                    <td className="r">{fmtNum(r.totalAbsentUnits)}</td>
                  </tr>
                );
              })}
              {showTotalRow && (
                <tr className="total-row">
                  <td colSpan={4} className="c">TỔNG CỘNG CHẤM NGÀY</td>
                  {daysArr.map((d) => (
                    <td key={d} className="c" style={{ padding: "2px" }}>
                      {dayTotals[d] ?? 0}
                    </td>
                  ))}
                  <td className="r">{fmtNum(sum.totalPresentUnits)}</td>
                  <td className="r">{fmtNum(sum.totalAbsentUnits)}</td>
                </tr>
              )}
            </tbody>
          </table>
        );
      }

      case "MEAL_SETTLEMENT":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Lớp</th>
                <th>Mã HS</th>
                <th>Họ tên học sinh</th>
                <th>Nguồn thu</th>
                <th>Đơn giá</th>
                <th>Suất ĐK</th>
                <th>Tiền ĐK</th>
                <th>Suất đã đóng</th>
                <th>Tiền đã đóng</th>
                <th>Suất thực tế</th>
                <th>Tiền thực tế</th>
                <th>Lệch suất</th>
                <th>Lệch tiền (+/-)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.className}</td>
                  <td className="c">{r.studentCode}</td>
                  <td className="l">{r.studentName}</td>
                  <td className="l">{r.incomeName}</td>
                  <td className="r">{fmtNum(r.unitPrice)}</td>
                  <td className="r">{fmtNum(r.expectedUnits)}</td>
                  <td className="r">{fmtNum(r.expectedAmount)}</td>
                  <td className="r">{fmtNum(r.paidUnits)}</td>
                  <td className="r">{fmtNum(r.paidAmount)}</td>
                  <td className="r">{fmtNum(r.actualUnits)}</td>
                  <td className="r">{fmtNum(r.actualAmount)}</td>
                  <td className="r">{fmtNum(r.diffUnits)}</td>
                  <td className="r">{fmtNum(r.diffAmount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={6} className="c">TỔNG CỘNG QUYẾT TOÁN SUẤT ĂN / BÁN TRÚ</td>
                <td className="r">{fmtNum(sum.expectedUnits)}</td>
                <td className="r">{fmtNum(sum.expectedAmount)}</td>
                <td className="r">{fmtNum(sum.paidUnits)}</td>
                <td className="r">{fmtNum(sum.paidAmount)}</td>
                <td className="r">{fmtNum(sum.actualUnits)}</td>
                <td className="r">{fmtNum(sum.actualAmount)}</td>
                <td className="c">-</td>
                <td className="r">{fmtNum((sum.excessDiffAmount || 0) - (sum.debtDiffAmount || 0))}</td>
              </tr>
            </tbody>
          </table>
        );

      case "CARRY_FORWARD_REPORT":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Lớp</th>
                <th>Mã HS</th>
                <th>Họ tên học sinh</th>
                <th>Thừa kỳ trước (+)</th>
                <th>Nợ kỳ trước (-)</th>
                <th>Phải thu trong kỳ</th>
                <th>Đã nộp trong kỳ</th>
                <th>Lệch tiền ăn</th>
                <th>Nợ học phí tháng</th>
                <th>Thừa chuyển kỳ sau (+)</th>
                <th>Nợ chuyển kỳ sau (-)</th>
                <th>Chi tiết kết chuyển</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.className}</td>
                  <td className="c">{r.studentCode}</td>
                  <td className="l">{r.studentName}</td>
                  <td className="r">{fmtNum(r.openingExcessAmount)}</td>
                  <td className="r">{fmtNum(r.openingDebtAmount)}</td>
                  <td className="r">{fmtNum(r.monthMustCollectAmount)}</td>
                  <td className="r">{fmtNum(r.monthPaidAmount)}</td>
                  <td className="r">{fmtNum(r.nextMealDiffAmount)}</td>
                  <td className="r">{fmtNum(r.nextMonthlyDebtAmount)}</td>
                  <td className="r">{fmtNum(r.closingExcessToNext)}</td>
                  <td className="r">{fmtNum(r.closingDebtToNext)}</td>
                  <td className="l">{r.itemsDetailSummary}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={4} className="c">TỔNG CỘNG KẾT CHUYỂN SANG KỲ SAU</td>
                <td className="r">{fmtNum(sum.openingExcessAmount)}</td>
                <td className="r">{fmtNum(sum.openingDebtAmount)}</td>
                <td className="r">{fmtNum(sum.monthMustCollectAmount)}</td>
                <td className="r">{fmtNum(sum.monthPaidAmount)}</td>
                <td className="r">{fmtNum(sum.nextMealDiffAmount)}</td>
                <td className="r">{fmtNum(sum.nextMonthlyDebtAmount)}</td>
                <td className="r">{fmtNum(sum.closingExcessToNext)}</td>
                <td className="r">{fmtNum(sum.closingDebtToNext)}</td>
                <td className="c"></td>
              </tr>
            </tbody>
          </table>
        );

      case "EINVOICE_CANCEL_REPORT":
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Ngày xuất</th>
                <th>Số HĐĐT</th>
                <th>Mã biên lai</th>
                <th>Mã HS</th>
                <th>Họ tên học sinh</th>
                <th>Lớp</th>
                <th>Nguồn thu</th>
                <th>Số tiền</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.actionDateStr}</td>
                  <td className="c">{r.invoidId}</td>
                  <td className="c">{r.billId}</td>
                  <td className="c">{r.studentCode}</td>
                  <td className="l">{r.studentName}</td>
                  <td className="c">{r.className}</td>
                  <td className="l">{r.incomeName}</td>
                  <td className="r">{fmtNum(r.amount)}</td>
                  <td className="c">{r.invoiceStatusLabel}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td colSpan={8} className="c">TỔNG CỘNG GIÁ TRỊ HÓA ĐƠN ĐIỆN TỬ</td>
                <td className="r">
                  {fmtNum(sum.displayedEinvoiceAmount ?? sum.totalEinvoiceAmount)}
                </td>
                <td className="c"></td>
              </tr>
            </tbody>
          </table>
        );

      case "STUDENT_ROSTER_REPORT":
      default:
        return (
          <table className="acc-print-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Khối</th>
                <th>Lớp học</th>
                <th>Tổng hồ sơ</th>
                <th>Đang học</th>
                <th>Nghỉ/Chuyển</th>
                <th>Nam</th>
                <th>Nữ</th>
                <th>Đã gắn Khối thu</th>
                <th>Chi tiết phân bổ Khối thu</th>
                <th>Đã lập chấm công</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, idx: number) => (
                <tr key={idx}>
                  <td className="c">{r.stt}</td>
                  <td className="c">{r.classLevel}</td>
                  <td className="l">{r.className}</td>
                  <td className="r">{fmtNum(r.totalStudents)}</td>
                  <td className="r">{fmtNum(r.activeStudents)}</td>
                  <td className="r">{fmtNum(r.inactiveStudents)}</td>
                  <td className="r">{fmtNum(r.maleStudents)}</td>
                  <td className="r">{fmtNum(r.femaleStudents)}</td>
                  <td className="r">{fmtNum(r.assignedRevenueBlockCount)}</td>
                  <td className="l">{r.revenueBlockBreakdown}</td>
                  <td className="r">{fmtNum(r.attendanceConfiguredCount)}</td>
                </tr>
              ))}
              {rows.length > 1 && (
                <tr className="total-row">
                  <td colSpan={3} className="c">TỔNG CỘNG SĨ SỐ</td>
                  <td className="r">{fmtNum(sum.totalStudents)}</td>
                  <td className="r">{fmtNum(sum.activeStudents)}</td>
                  <td className="r">{fmtNum(sum.inactiveStudents)}</td>
                  <td className="r">{fmtNum(sum.maleStudents)}</td>
                  <td className="r">{fmtNum(sum.femaleStudents)}</td>
                  <td className="r">{fmtNum(sum.assignedRevenueBlockCount)}</td>
                  <td className="c">-</td>
                  <td className="r">{fmtNum(sum.attendanceConfiguredCount)}</td>
                </tr>
              )}
            </tbody>
          </table>
        );
    }
  }, [open, reportData]);

  if (!open || !reportData) return null;

  const today = new Date();
  const dateLine = `......, ngày ${String(today.getDate()).padStart(2, "0")} tháng ${String(
    today.getMonth() + 1
  ).padStart(2, "0")} năm ${today.getFullYear()}`;

  const cleanupPrintIframe = () => {
    const oldIframe = document.getElementById(PRINT_IFRAME_ID);
    if (oldIframe && oldIframe.parentNode) {
      oldIframe.parentNode.removeChild(oldIframe);
    }
  };

  const handleModalClose = () => {
    cleanupPrintIframe();
    onCancel();
  };

  const handlePrintOrSavePdf = () => {
    if (!printContentRef.current) return;
    const htmlContent = printContentRef.current.innerHTML;

    cleanupPrintIframe();

    const iframe = document.createElement("iframe");
    iframe.id = PRINT_IFRAME_ID;
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(`<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="utf-8" />
  <title>${reportData.reportCode} - ${reportData.reportTitle}</title>
  <style>
    @page {
      size: A4 ${orientation};
      margin: 0mm 10mm;
    }
    body {
      margin: 0;
      padding: 10mm 0;
      font-family: "Times New Roman", Times, serif;
      font-size: 11pt;
      color: #000000;
      background: #ffffff;
    }
    table.acc-print-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      font-size: 9.5pt;
    }
    table.acc-print-table thead {
      display: table-header-group;
    }
    table.acc-print-table th,
    table.acc-print-table td {
      border: 1px solid #000000;
      padding: 4px 5px;
      vertical-align: middle;
    }
    table.acc-print-table th {
      background-color: #e2e8f0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-weight: bold;
      text-align: center;
    }
    table.acc-print-table td.c { text-align: center; }
    table.acc-print-table td.l { text-align: left; padding: 4px 6px; }
    table.acc-print-table td.r { text-align: right; padding: 4px 6px; }
    tr.total-row {
      page-break-inside: avoid;
    }
    tr.total-row td {
      background-color: #fef9c3 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-weight: bold;
    }
    .signature-block {
      margin-top: 16px;
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  ${htmlContent}
</body>
</html>`);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    }, 50);
  };

  const sum = reportData.summary || {};

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      destroyOnClose
      width={1180}
      title={`Xem trước Bản in A4 & Xuất PDF — ${reportData.reportCode}: ${reportData.reportTitle}`}
      footer={[
        <Button key="close" onClick={handleModalClose}>
          Đóng
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrintOrSavePdf}
        >
          In Báo cáo / Lưu file PDF (A4)
        </Button>,
      ]}
    >
      <div className="a4-preview-toolbar">
        <Space wrap>
          <span style={{ fontWeight: 600 }}>Khổ giấy A4:</span>
          <Radio.Group
            value={orientation}
            onChange={(e) => setOrientation(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="landscape">A4 Ngang (Landscape)</Radio.Button>
            <Radio.Button value="portrait">A4 Dọc (Portrait)</Radio.Button>
          </Radio.Group>
        </Space>

        <Space wrap>
          <Input
            size="small"
            placeholder="Tên Người lập biểu..."
            value={preparerName}
            onChange={(e) => setPreparerName(e.target.value)}
            style={{ width: 165 }}
          />
          <Input
            size="small"
            placeholder="Tên Kế toán trưởng..."
            value={chiefAccountantName}
            onChange={(e) => setChiefAccountantName(e.target.value)}
            style={{ width: 165 }}
          />
          <Input
            size="small"
            placeholder="Tên Hiệu trưởng..."
            value={principalName}
            onChange={(e) => setPrincipalName(e.target.value)}
            style={{ width: 165 }}
          />
        </Space>
      </div>

      <div className="a4-preview-viewport">
        <div ref={printContentRef} className={`a4-paper-sheet ${orientation}`}>
          {/* Top Accounting Header */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ width: "52%" }}>
              <div style={{ fontWeight: "bold", fontSize: "11pt", textTransform: "uppercase" }}>
                {reportData.schoolName || "ĐƠN VỊ TRƯỜNG HỌC"}
              </div>
              {reportData.schoolAddress && (
                <div style={{ fontSize: "10pt", marginTop: 2 }}>
                  Địa chỉ: {reportData.schoolAddress}
                </div>
              )}
              <div style={{ fontSize: "10pt", marginTop: 2 }}>
                {reportData.schoolTaxCode ? `Mã số thuế: ${reportData.schoolTaxCode}  |  ` : ""}
                Mẫu biểu: <strong>{reportData.reportCode}</strong>
              </div>
            </div>

            <div style={{ width: "46%", textAlign: "center" }}>
              <div style={{ fontWeight: "bold", fontSize: "11pt" }}>
                CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
              </div>
              <div style={{ fontWeight: "bold", fontStyle: "italic", fontSize: "10.5pt", textDecoration: "underline" }}>
                Độc lập - Tự do - Hạnh phúc
              </div>
            </div>
          </div>

          {/* Title Block */}
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontWeight: "bold", fontSize: "14pt", textTransform: "uppercase" }}>
              {reportData.reportTitle}
            </div>
            <div style={{ fontStyle: "italic", fontSize: "10.5pt", marginTop: 3 }}>
              {reportData.periodLabel}
            </div>
          </div>

          {/* Data Table */}
          {renderedTable}

          {/* Amount in Words */}
          <div style={{ marginTop: 10, fontSize: "10.5pt" }}>
            {sum.paidAmountInWords && (
              <div style={{ marginBottom: 3 }}>
                <em>Tổng số tiền đã thu bằng chữ: </em>
                <strong>{sum.paidAmountInWords}</strong>
              </div>
            )}
            {sum.remainingAmountInWords && (
              <div style={{ marginBottom: 3 }}>
                <em>Tổng số tiền còn phải thu bằng chữ: </em>
                <strong>{sum.remainingAmountInWords}</strong>
              </div>
            )}
            {sum.actualAmountInWords && (
              <div style={{ marginBottom: 3 }}>
                <em>Tổng giá trị thực tế sử dụng bằng chữ: </em>
                <strong>{sum.actualAmountInWords}</strong>
              </div>
            )}
            {sum.closingExcessInWords && (
              <div style={{ marginBottom: 3 }}>
                <em>Tổng tiền thừa kết chuyển sang kỳ sau bằng chữ: </em>
                <strong>{sum.closingExcessInWords}</strong>
              </div>
            )}
            {sum.closingDebtInWords && (
              <div style={{ marginBottom: 3 }}>
                <em>Tổng tiền nợ kết chuyển sang kỳ sau bằng chữ: </em>
                <strong>{sum.closingDebtInWords}</strong>
              </div>
            )}
          </div>

          {/* 3-Column Accounting Signature Block */}
          <div className="signature-block" style={{ marginTop: 16, pageBreakInside: "avoid" }}>
            <div style={{ textAlign: "right", fontStyle: "italic", fontSize: "10.5pt", marginBottom: 6 }}>
              {dateLine}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", textAlign: "center" }}>
              <div style={{ width: "32%" }}>
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>NGƯỜI LẬP BIỂU</div>
                <div style={{ fontStyle: "italic", fontSize: "9.5pt" }}>(Ký, ghi rõ họ tên)</div>
                <div style={{ height: 65 }} />
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>{preparerName}</div>
              </div>
              <div style={{ width: "32%" }}>
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>KẾ TOÁN TRƯỞNG</div>
                <div style={{ fontStyle: "italic", fontSize: "9.5pt" }}>(Ký, ghi rõ họ tên)</div>
                <div style={{ height: 65 }} />
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>{chiefAccountantName}</div>
              </div>
              <div style={{ width: "34%" }}>
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>HIỆU TRƯỞNG</div>
                <div style={{ fontStyle: "italic", fontSize: "9.5pt" }}>(Ký, đóng dấu, ghi rõ họ tên)</div>
                <div style={{ height: 65 }} />
                <div style={{ fontWeight: "bold", fontSize: "10.5pt" }}>{principalName}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReportPrintPreviewModal;
